import { useTranslation } from 'react-i18next';
import '@/utils/notifications';
import type { ProgramFormData } from '../shared/ProgramFormConfig';
import ProgramFormContainer from '../shared/ProgramFormContainer';
import { CreateProgramMutation } from './CreateProgram.graphql';
import type { CreateProgramMutationVariables } from './CreateProgram.graphql';
import client from '@/apolloClient';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

export interface CreateProgramProps {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const CreateProgram = ({ open, onClose, onCreated }: CreateProgramProps) => {
    const { t } = useTranslation('programManagementPage');

    const handleSubmit = async (formData: ProgramFormData) => {
        try {
            const variables: CreateProgramMutationVariables = {
                input: formData,
            };

            const { data } = await client.mutate({
                mutation: CreateProgramMutation,
                variables,
            });

            if (data?.createProgram?.__typename === 'CreateProgramSuccessfulResponse') {
                if (onCreated) {
                    onCreated();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('notifications.successTitle'),
                    description: t('notifications.successDescription', { name: formData.name }),
                });
                return;
            }

            if (data?.createProgram?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.createProgram.fields,
                };
                showErrorNotification({
                    message: t('notifications.errorTitle'),
                    description: error.fields?.[0]?.message || t('notifications.errorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error creating program');
        } catch (error) {
            console.error('Error creating program:', error);
            // Handle error appropriately
        }
    };
    return (
        <ProgramFormContainer
            mode="create"
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            title={t('modalCreateProgram.title')}
        />
    );
};

export default CreateProgram;
